import { Injectable, computed, inject, signal } from '@angular/core';
import {
  Observable,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  of,
  tap,
  throwError,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { LoginResponse } from '@ls-commons/auth/model';

export interface UserJWT {
  name: string;
  surname: string;
  role: number;
  id: number;
  image: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _currentUser = signal<UserJWT | null>(null);
  public currentUser = computed(() => this._currentUser());

  readonly http = inject(HttpClient);
  readonly router = inject(Router);
  socialAuthService = inject(SocialAuthService);
  googleToken = '';

  socialAuthState$ = this.socialAuthService.authState.pipe(
    map((user: SocialUser) => user?.idToken),
    distinctUntilChanged(),
    filter(token => token !== this.googleToken),
    tap(token => (this.googleToken = token))
  );

  constructor() {
    const token = this.getToken();
    token && this.retriveUserFromJWT(token);
  }

  retriveUserFromJWT(token: string) {
    const helper = new JwtHelperService();
    const tokenData = helper.decodeToken(token);
    this._currentUser.set({
      name: tokenData.name,
      surname: tokenData.surname,
      role: tokenData.role,
      id: tokenData.userId,
      image: tokenData.image,
    });
  }

  login(email: string, password: string): Observable<LoginResponse> {
    const url = 'usuario/login';
    const body = { email, contrasena: password };

    return this.http
      .post<LoginResponse>(url, body)
      .pipe(catchError(err => this.handleError(err, 'Login')));
  }

  refreshToken(): Observable<boolean> {
    const url = 'usuario/login/renew';

    return this.http.get<LoginResponse>(url).pipe(
      map((resp: LoginResponse) => {
        this.setAuthentication(null, resp.token);
        return true;
      }),
      catchError(() => of(false))
    );
  }

  loginGoogle(token: string): Observable<LoginResponse> {
    const url = 'usuario/login-google';
    const body = { token };

    return this.http
      .post<LoginResponse>(url, body)
      .pipe(catchError(err => this.handleError(err, 'Login Google')));
  }

  resetPassword(email: string): Observable<Response> {
    const url = 'usuario/restablecer-contrasena';
    const body = { email };

    return this.http
      .post<Response>(url, body)
      .pipe(catchError(err => this.handleError(err, 'Reset Password')));
  }

  checkTokenReset(token: string): Observable<Response> {
    const url = `usuario/restablecer-contrasena/${token}`;

    return this.http
      .get<Response>(url)
      .pipe(catchError(err => this.handleError(err, 'Check Token Reset')));
  }

  newPassword(token: string, password: string): Observable<Response> {
    const url = `usuario/restablecer-contrasena/${token}`;
    const body = { token, password };
    return this.http
      .post<Response>(url, body)
      .pipe(catchError(err => this.handleError(err, 'New Password')));
  }

  removeStorage() {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
  }

  logout() {
    this.removeStorage();
    this.router.navigate(['/']);
    this.socialAuthService.signOut();
  }

  setAuthentication(remember: boolean | null, token: string) {
    if (remember === null) {
      remember = localStorage.getItem('token') ? true : false;
    }
    this.removeStorage();
    remember
      ? localStorage.setItem('token', token)
      : sessionStorage.setItem('token', token);
    this.retriveUserFromJWT(token);
  }

  getToken() {
    return (
      localStorage.getItem('token') || sessionStorage.getItem('token') || ''
    );
  }

  private handleError(err: any, error: string) {
    return throwError(() =>
      err.status === 400 ? err.error.msg : `Se ha producido un error: ${error}`
    );
  }
}
